import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Star, StarFilled, Upload } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import copy from 'copy-to-clipboard'
import { Features } from 'generated/featureDefinitions'
import {
  AtsContractTypesEnum,
  AtsCreateJobPostingFavouriteDocument,
  AtsDeleteJobPostingFavouriteDocument,
  AtsDuplicateJobPostingDocument,
  AtsFavouriteJobPostingItemFragment,
  AtsJobPostingItemFragment,
  AtsJobPostingsHiringManagersDocument,
  AtsJobPostingsTableDataDocument,
  AtsJobsCrontractTypesDocument,
  AtsJobsListDocument,
  AtsJobsLocationsDocument,
  AtsJobsPageDocument,
  AtsJobsTeamsDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { buildUrl as buildAvatarUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { statusColor, statusLabel } from 'modules/ats/components/JobStatusTag'
import { DeleteJobPostingModal } from 'modules/ats/components/job_postings/DeleteJobPostingModal/DeleteJobPostingModal'
import { useAtsContext } from 'modules/ats/lib/atsContext'
import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'

interface JobsTableProps {
  onAddFavourite: (status: boolean) => void
  favouriteJobs: AtsFavouriteJobPostingItemFragment[] | null | undefined
  onDeleteFavourite: (jobId: number | null) => void
}

const DEFAULT_PAGE_SIZE = 20

const JobsTable = ({ onAddFavourite, favouriteJobs, onDeleteFavourite }: JobsTableProps) => {
  const [openedModal, setOpenedModal] = useState<'delete' | undefined>(undefined)
  const [selectedJobId, setSelectedJobId] = useState<number | undefined>(undefined)
  const { companyPageReady } = useAtsContext()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const { buildUrl } = useCompanyPageSettings()
  const {
    access: { id: authorId },
    company: { id: companyId },
  } = useCurrentSession()

  interface JobsTableFilters {
    status?: string[]
    scheduleType?: string[]
    contractType?: AtsContractTypesEnum[]
    workplaceType?: string[]
    hiringManagerAccessIds?: number[]
    locationIds?: number[]
    teamIds?: number[]
  }

  const { data: jobPostingsTableData } = useQuery(AtsJobPostingsTableDataDocument, {
    variables: {
      companyId,
    },
  })

  const isRequisitionsFeatureEnabled =
    jobPostingsTableData?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.name === Features.ats_job_requisitions
    )?.available

  const requisitionsEnabled =
    jobPostingsTableData?.companies.featureAvailabilitiesConnection.nodes.at(0)?.available ?? false

  const isRequisitionsEnabled = isRequisitionsFeatureEnabled && requisitionsEnabled

  const canUseRequisitions = jobPostingsTableData?.ats._policies.requisitionsUse.can
  const canCreateJobPostings =
    jobPostingsTableData?.ats._policies.createJobPostings.can && !isRequisitionsEnabled
  const canManageJobPostings = jobPostingsTableData?.ats._policies.manageJobPostings.can
  const canImportResources = jobPostingsTableData?.universalImporter?._policies.importResources.can
  const atsImportersFeatureFlag = jobPostingsTableData?.features.featuresConnection.nodes.find(
    (featureFlag) => featureFlag.name === 'ats_importers'
  )

  const showRequisitionsButton =
    isRequisitionsEnabled && canUseRequisitions && !canManageJobPostings
  const showCreateJobButton =
    (canManageJobPostings || (!isRequisitionsEnabled && canCreateJobPostings)) ?? false
  const showImportResources = !!(canImportResources && atsImportersFeatureFlag?.enabled)

  const [savedFilters, setSavedFilters] = useLocalStorage<JobsTableFilters | undefined>(
    LOCAL_STORAGE_KEYS.jobsTableFilters
  )

  const showDeleteJobButton = canManageJobPostings || canCreateJobPostings

  const copyUrl = async (jobId: number) => {
    const url = buildUrl(`/job_posting/${jobId}`)

    if (url && copy(url)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.job_details.url_copied'),
      })
    }
  }

  const [createAtsFavouriteJobPosting] = useMutation(AtsCreateJobPostingFavouriteDocument, {
    extractorFn: (data) => data.atsMutations.createAtsFavouriteJobPosting,
    update(cache, result) {
      const newFavourite =
        result.data?.atsMutations?.createAtsFavouriteJobPosting?.favouriteJobPosting
      if (!newFavourite) return

      const queryOptions = {
        query: AtsJobsPageDocument,
        variables: { authorId },
      }

      const existingData = cache.readQuery(queryOptions)
      if (!existingData?.ats) return

      const updatedData = {
        ...existingData,
        ats: {
          ...existingData.ats,
          favouriteJobPostingsConnection: {
            ...existingData.ats.favouriteJobPostingsConnection,
            nodes: [newFavourite, ...(existingData.ats.favouriteJobPostingsConnection.nodes || [])],
          },
        },
      }

      cache.writeQuery({
        ...queryOptions,
        data: updatedData,
      })
    },
  })

  const [duplicateAtsJobPosting] = useMutation(AtsDuplicateJobPostingDocument, {
    extractorFn: (data) => data.atsMutations.duplicateAtsJobPosting,
  })

  const [deleteAtsFavouriteJobPosting] = useMutation(AtsDeleteJobPostingFavouriteDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsFavouriteJobPosting,
  })

  const onFavouriteJob = async (jobId: number) => {
    onAddFavourite(true)
    const result = await createAtsFavouriteJobPosting({
      variables: { atsJobPostingId: jobId, authorId },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onAddFavourite(false)
    return handler
  }

  const onDuplicate = async (jobId: number) => {
    const result = await duplicateAtsJobPosting({
      variables: { id: jobId, authorId },
      refetchQueries: [AtsJobsListDocument],
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      if (handler.data?.jobPosting?.id) {
        navigate(
          navigation.ats.jobs.jobsId.settings.fullPath({
            jobId: handler.data.jobPosting.id,
          })
        )
      }
    }

    return handler
  }

  const onDeleteFavouriteJob = async (favouriteJob: AtsFavouriteJobPostingItemFragment) => {
    onDeleteFavourite(favouriteJob.id)
    const result = await deleteAtsFavouriteJobPosting({
      variables: { id: favouriteJob.id, authorId },
      update(cache) {
        cache.evict({ id: cache.identify(favouriteJob) })
      },
    })

    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    onDeleteFavourite(null)

    return handler
  }

  const onDeleteJob = (jobId: number) => {
    setSelectedJobId(jobId)
    setOpenedModal('delete')
  }

  const source = useGraphqlDataCollectionSource({
    document: AtsJobsListDocument,
    mapData: (data) => data.ats.jobPostingsConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.jobPostingsConnection.totalCount ?? 0,
    }),
    perPage: DEFAULT_PAGE_SIZE,

    currentFilters: savedFilters ?? { status: ['published', 'draft', 'unlisted'] },

    mapVariables: ({ search, filters, sortings }) => {
      const status =
        filters.status && filters.status.length > 0 ? { in: filters.status } : undefined
      const scheduleType =
        filters?.scheduleType && filters?.scheduleType.length > 0
          ? { in: filters.scheduleType }
          : undefined
      const contractType =
        filters?.contractType && filters?.contractType.length > 0
          ? { in: filters.contractType }
          : undefined
      const workplaceType =
        filters?.workplaceType && filters?.workplaceType.length > 0
          ? { in: filters.workplaceType }
          : undefined
      const hiringManagerAccessIds =
        filters?.hiringManagerAccessIds && filters?.hiringManagerAccessIds.length > 0
          ? filters.hiringManagerAccessIds.map(Number).filter(Boolean)
          : undefined
      const locationIds =
        filters?.locationIds && filters?.locationIds.length > 0
          ? filters.locationIds.map(Number).filter(Boolean)
          : undefined
      const teamIds =
        filters?.teamIds && filters?.teamIds.length > 0
          ? filters.teamIds.map(Number).filter(Boolean)
          : undefined

      setSavedFilters(filters)

      return {
        search,
        status,
        scheduleType,
        contractType,
        workplaceType,
        hiringManagerAccessIds,
        locationIds,
        teamIds,
        sort: sortings?.[0],
      }
    },
    sortings: {
      title: {
        label: i18n.t('ats.table_headers.offers'),
        sortBy: 'title',
      },
      publishedAt: {
        label: i18n.t('ats.table_headers.published_at'),
        sortBy: 'publishedAt',
      },
      status: {
        label: i18n.t('ats.table_headers.status'),
        sortBy: 'status',
      },
      workplaceType: {
        label: i18n.t('ats.table_headers.workplace_type'),
        sortBy: 'workplaceType',
      },
      locationId: {
        label: i18n.t('ats.table_headers.location'),
        sortBy: 'locationId',
      },
    },
    defaultSortings: {
      field: 'publishedAt',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    filters: {
      status: {
        label: i18n.t('ats.table_headers.status'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.job_status.active'), value: 'published' },
            { label: i18n.t('ats.job_status.unlisted'), value: 'unlisted' },
            { label: i18n.t('ats.job_status.draft'), value: 'draft' },
            { label: i18n.t('ats.job_status.archived'), value: 'archived' },
            { label: i18n.t('ats.job_status.cancelled'), value: 'cancelled' },
          ],
        },
      },
      scheduleType: {
        label: i18n.t('ats.table_headers.schedule_type'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.job_schedule.full_time'), value: 'full_time' },
            { label: i18n.t('ats.job_schedule.part_time'), value: 'part_time' },
          ],
        },
      },
      contractType: {
        label: i18n.t('ats.table_headers.contract_type'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobsCrontractTypesDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.contractTypesConnection.nodes.map((contractType) => ({
                label: contractType.label || '',
                value: contractType.id,
              })) || []
            )
          },
        },
      },
      workplaceType: {
        label: i18n.t('ats.table_headers.workplace_type'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.workplace_types.onsite'), value: 'onsite' },
            { label: i18n.t('ats.workplace_types.remote'), value: 'remote' },
            { label: i18n.t('ats.workplace_types.hybrid'), value: 'hybrid' },
          ],
        },
      },
      hiringManagerAccessIds: {
        label: i18n.t('ats.table_headers.hiring_manager'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobPostingsHiringManagersDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.hiringManagers.map((manager) => ({
                label: manager.access?.fullName || '',
                value: Number(manager.access?.id),
              })) || []
            ).sort((a, b) => a.label.localeCompare(b.label))
          },
        },
      },
      locationIds: {
        label: i18n.t('ats.table_headers.location'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobsLocationsDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.locations.locationsConnection.nodes.map((location) => ({
                label: location.name || '',
                value: Number(location.id),
              })) || []
            )
          },
        },
      },
      teamIds: {
        label: i18n.t('ats.table_headers.team'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsJobsTeamsDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.teams.teamsConnection.edges.map((edge) => ({
                label: edge.node.name || '',
                value: Number(edge.node.id),
              })) || []
            )
          },
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.job_status.active'),
        filter: { status: ['published'] },
      },
      {
        label: i18n.t('ats.job_status.unlisted'),
        filter: { status: ['unlisted'] },
      },
      {
        label: i18n.t('ats.job_status.draft'),
        filter: { status: ['draft'] },
      },
      {
        label: i18n.t('ats.job_status.archived'),
        filter: { status: ['archived'] },
      },
    ],
    itemUrl: ({ id: jobId }) => navigation.ats.jobs.jobsId.applications.fullPath({ jobId }),
    itemActions: ({ id: jobId, status }) => {
      const canDuplicateJobs = showCreateJobButton
      const favouriteJob = favouriteJobs?.find((favJob) => favJob.atsJobPosting.id === jobId)
      const isJobPostingOpen = status === 'published' || status === 'unlisted'

      return [
        favouriteJob
          ? {
              label: i18n.t('ats.jobs.favourites.remove_favourite'),
              onClick: () => onDeleteFavouriteJob(favouriteJob),
              icon: StarFilled,
              critical: true,
            }
          : {
              label: i18n.t('ats.jobs.favourites.add_favourite'),
              onClick: () => onFavouriteJob(jobId),
              icon: Star,
            },
        (canDuplicateJobs || companyPageReady) && {
          type: 'separator' as const,
        },
        canDuplicateJobs && {
          label: i18n.t('ats.job_details.duplicate_job'),
          onClick: () => onDuplicate(jobId),
        },
        companyPageReady && {
          label: i18n.t('ats.job_details.copy_url'),
          onClick: () => copyUrl(jobId),
        },
        showDeleteJobButton &&
          !isJobPostingOpen && {
            label: i18n.t('common.delete'),
            onClick: () => onDeleteJob(jobId),
            icon: Delete,
            critical: true,
          },
      ].filter(Boolean)
    },
    primaryActions: () => {
      if (showCreateJobButton) {
        return {
          label: i18n.t('ats.jobs.new_job'),
          icon: Add,
          onClick: () => navigate(navigation.ats.jobs.new.fullPath()),
        }
      }
      if (showRequisitionsButton) {
        return {
          label: i18n.t('ats.jobs.request_new_job'),
          icon: Add,
          onClick: () => navigate(navigation.ats.jobs.requisitions.new.fullPath()),
        }
      }
      return undefined
    },
    secondaryActions: () => {
      if (showImportResources) {
        return [
          {
            label: i18n.t('ats.jobs.jobs_importer'),
            icon: Upload,
            onClick: () =>
              navigate(navigation.ats.jobs.importJobPostings.fullPath().replace(/\*+$/, '')),
          },
        ]
      }
      return undefined
    },
  })

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('ats.table_headers.offers'),
                  render: ({ title, team }: AtsJobPostingItemFragment) => {
                    return `${title} ${team?.name ? `(${team?.name})` : ''}`
                  },
                  sorting: 'title',
                },
                {
                  label: i18n.t('ats.table_headers.location'),
                  render: ({ location }: AtsJobPostingItemFragment) => location?.name || '-',
                  sorting: 'locationId',
                },
                {
                  label: i18n.t('ats.table_headers.workplace_type'),
                  render: ({ workplaceTypeLabel }: AtsJobPostingItemFragment) =>
                    workplaceTypeLabel || '-',
                  sorting: 'workplaceType',
                },
                {
                  label: i18n.t('ats.table_headers.number_of_vacancies'),
                  render: ({ numberOfVacancies }: AtsJobPostingItemFragment) => {
                    return {
                      type: 'tag',
                      value: { label: numberOfVacancies?.toString() || '-' },
                    }
                  },
                },
                {
                  label: i18n.t('ats.table_headers.hiring_manager'),
                  render: ({ hiringmanagersConnection }: AtsJobPostingItemFragment) => {
                    const hiringManagers = hiringmanagersConnection.nodes
                    return {
                      type: 'avatarList',
                      value: {
                        avatarList: hiringManagers.map((hiringManager) => ({
                          type: 'person',
                          firstName: hiringManager.access?.firstName || '',
                          lastName: hiringManager.access?.lastName || '',
                          src: buildAvatarUrl(hiringManager.access?.avatar || undefined),
                        })),
                        max: 5,
                      },
                    }
                  },
                },
                {
                  label: i18n.t('ats.table_headers.candidates'),
                  render: ({ applicationphasesConnection }: AtsJobPostingItemFragment) => {
                    const phases =
                      applicationphasesConnection?.nodes.filter(
                        (phase) =>
                          phase?.activeApplicationsCount && phase?.activeApplicationsCount > 0
                      ) || []

                    if (phases.length > 0) {
                      return {
                        type: 'tagList',
                        value: {
                          type: 'dot',
                          max: 3,
                          tags: phases.map((phase) => ({
                            text: phase.activeApplicationsCount?.toString() ?? '0',
                            color: phaseColor(phase.hiringStage?.name || 'default'),
                            description: phase?.name,
                          })),
                        },
                      }
                    }

                    return {
                      type: 'tag',
                      value: { label: i18n.t('ats.table_jobs.no_candidates') },
                    }
                  },
                },
                {
                  label: i18n.t('ats.table_headers.published_at'),
                  render: ({ publishedAt }: AtsJobPostingItemFragment) =>
                    publishedAt ? dateBackendDateTimeToRelativeTime(publishedAt.toString()) : '-',
                  sorting: 'publishedAt',
                },
                {
                  label: i18n.t('ats.table_headers.start_date'),
                  render: ({ startDate }: AtsJobPostingItemFragment) =>
                    startDate ? dateBackendDateTimeToRelativeTime(startDate.toString()) : '-',
                },
                {
                  label: i18n.t('ats.table_headers.status'),
                  render: ({ status }: AtsJobPostingItemFragment) => ({
                    type: 'status',
                    value: {
                      status: statusColor(status || 'cancelled'),
                      label: statusLabel(status || 'cancelled'),
                    },
                  }),
                  sorting: 'status',
                },
              ],
            },
          },
        ]}
        emptyStates={{
          'no-data': {
            emoji: '💼',
            title: i18n.t('ats.table_jobs.empty_state.title'),
            description: i18n.t('ats.table_jobs.empty_state.description'),
            actions: [
              {
                label: i18n.t('ats.table_jobs.empty_state.view_demo_label'),
                variant: 'outline' as const,
                onClick: () => {
                  window.open(i18n.t('ats.table_jobs.empty_state.demo_url'), '_self')
                },
              },
              showCreateJobButton && {
                label: i18n.t('ats.table_jobs.empty_state.create_job_label'),
                onClick: () => navigate(navigation.ats.jobs.new.fullPath()),
              },
            ].filter(Boolean),
          },
        }}
      />
      {openedModal === 'delete' && selectedJobId && (
        <DeleteJobPostingModal
          id={selectedJobId}
          onClose={() => {
            setSelectedJobId(undefined)
            setOpenedModal(undefined)
          }}
        />
      )}
    </>
  )
}

export default JobsTable
